import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  VARIABLE_TEXT_FEATURE_FLAG_ALL,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import { DEFAULT_SECTION_BACKGROUND_GROUP } from '../default/section.default'
import {
  BuilderComponentGroup,
  BuilderComponentSettingType,
  ComponentSettingKey,
  MdShape,
  SpacingInputUnit,
} from '../types'
import { LABELS } from '../types/labels'
import {
  hoverEffectOptions,
  makeClampHeightItems,
  makeClampWidthItems,
  makeSpacing,
  makeTextColorSettings,
  makeTypographyGroup,
} from '../utils/components'

export const ContainerComponents: BuilderComponentGroup = {
  id: 'container',
  title: LABELS.CONTAINERS_KEY,
  components: [
    {
      compId: 'section',
      title: LABELS.SECTION_KEY,
      searchKeywords: [
        'container',
        'block',
        'row',
        'column',
        'wrapper',
        'layout',
        'page section',
        'grouping',
        'area',
        'region',
      ],
      icon: 'Section24',
      nested: true,
      settings: {
        designGroup: {
          [LABELS.VARIANTS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignVariant,
                label: LABELS.VARIANT_KEY,
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    { value: 'normal', label: 'Regular Section' },
                    { value: 'sticky', label: 'Sticky Banner' },
                  ],
                },
                defaultValue: 'normal',
              },
              {
                id: ComponentSettingKey.SectionDesignStickyPosition,
                label: LABELS.STICKY_LOCATION_KEY,
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    { value: 'top', label: 'Aligned to Top' },
                    { value: 'bottom', label: 'Aligned to Bottom' },
                  ],
                },
                defaultValue: 'top',
                dependOn: {
                  id: ComponentSettingKey.SectionDesignVariant,
                  showWhen: (v) => v === 'sticky',
                },
              },
              {
                id: ComponentSettingKey.SectionDesignStickyOffset,
                label: 'Sticky Offset',
                type: BuilderComponentSettingType.Number,
                defaultValue: 0,
                numberSettings: {
                  min: 0,
                  max: 100,
                },
                dependOn: {
                  id: ComponentSettingKey.SectionDesignVariant,
                  showWhen: (v) => v === 'sticky',
                },
              },
            ],
          },
          [LABELS.ELEVATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignElevation,
                label: LABELS.ELEVATION_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: false,
              },
            ],
          },
          [LABELS.BACKGROUND_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignBackground,
                label: '',
                type: BuilderComponentSettingType.SectionBackgroundGroup,
                colorVariantSettings: {
                  schemeId: ComponentSettingKey.SectionDesignBgScheme,
                },
                imageSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.ColumnData |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.Image,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.URL, ColumnTypes.Lookup],
                },
                videoSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.ColumnData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.CustomText,
                  columnTypes: [ColumnTypes.URL, ColumnTypes.Attachment],
                },
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell | VariableTextFeatureFlag.CurrentRow,
                },
                defaultValue: DEFAULT_SECTION_BACKGROUND_GROUP as any,
              },
            ],
          },
          [LABELS.LAYOUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignLayout,
                label: LABELS.COLUMN_KEY,
                type: BuilderComponentSettingType.LayoutColumnSelect,
                defaultValue: [12],
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignShape,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.None,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignWidthPreset,
                label: LABELS.WIDTH_KEY,
                type: BuilderComponentSettingType.SegmentedSelect,
                defaultValue: '100',
                segmentedSelectSettings: {
                  options: [
                    { value: '40', label: '40%' },
                    { value: '50', label: '50%' },
                    { value: '60', label: '60%' },
                    { value: '70', label: '70%' },
                    { value: '80', label: '80%' },
                    { value: '90', label: '90%' },
                    { value: '100', label: '100%' },
                  ],
                },
              },
              // Height Mode Selector
              {
                id: ComponentSettingKey.SectionDesignHeightMode,
                label: 'Height Mode',
                type: BuilderComponentSettingType.Select,
                defaultValue: 'auto',
                selectSettings: {
                  options: [
                    { value: 'auto', label: 'Auto' },
                    { value: 'custom', label: 'Custom' },
                    { value: 'full-viewport', label: 'Full Viewport' },
                  ],
                },
              },
              ...makeClampHeightItems(ComponentSettingKey.SectionDesignHeight, {
                defaultValue: 400,
                defaultUnit: 'px',
                label: LABELS.HEIGHT_KEY,
              }).items.map((item) => ({
                ...item,
                dependOn: {
                  id: ComponentSettingKey.SectionDesignHeightMode,
                  showWhen: (v: any) => v === 'custom',
                },
              })),
            ],
          },
          [LABELS.ORIENTATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignOrientation,
                label: LABELS.ORIENTATION_KEY,
                type: BuilderComponentSettingType.IconSelect,
                defaultValue: 'vertical',
                help: '',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: LABELS.HORIZONTAL_KEY,
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: LABELS.VERTICAL_KEY,
                    },
                  ],
                },
              },
              {
                id: ComponentSettingKey.SectionDesignReverseOrder,
                label: LABELS.REVERSE_ORDER_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: false,
              },
              // {
              //   id: ComponentSettingKey.SectionDesignMaxItems,
              //   label: LABELS.MAX_ITEMS_KEY,
              //   type: BuilderComponentSettingType.Number,
              //   defaultValue: 1,
              //   numberSettings: {
              //     min: 1,
              //     max: 10,
              //   },
              //   dependOn: {
              //     id: ComponentSettingKey.SectionDesignOrientation,
              //     showWhen: (v) => v === 'horizontal',
              //   },
              // },
              // {
              //   id: ComponentSettingKey.SectionDesignSameHeight,
              //   label: LABELS.FORCE_EQUAL_HEIGHT_KEY,
              //   type: BuilderComponentSettingType.Switch,
              //   help: 'Only works is horizontal orientation',
              //   defaultValue: true,
              //   dependOn: {
              //     id: ComponentSettingKey.SectionDesignOrientation,
              //     showWhen: (v) => v === 'horizontal',
              //   },
              // },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.SectionDesignVAlign,
                type: BuilderComponentSettingType.VerticalAlignment,
                defaultValue: 'middle',
                label: LABELS.VERTICAL_ALIGNMENT_KEY,
                dependOn: {
                  id: ComponentSettingKey.SectionDesignHeightMode,
                  showWhen: (v: any) => v !== 'full-viewport',
                },
              },
              {
                id: ComponentSettingKey.SectionDesignHorizontalAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.HORIZONTAL_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.BORDER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignBorder,
                type: BuilderComponentSettingType.IconSelect,
                label: LABELS.SHOW_BORDER_KEY,
                defaultValue: ['top', 'inline-end', 'bottom', 'inline-start'],
                iconSelectSettings: {
                  multiple: true,
                  options: [
                    {
                      id: 'top',
                      icon: 'BorderTop24',
                      title: LABELS.BORDER_TOP_KEY,
                    },
                    {
                      id: 'inline-end',
                      icon: 'BorderRight24',
                      title: LABELS.BORDER_END_KEY,
                    },
                    {
                      id: 'bottom',
                      icon: 'BorderBot24',
                      title: LABELS.BORDER_BOTTOM_KEY,
                    },
                    {
                      id: 'inline-start',
                      icon: 'BorderLeft24',
                      title: LABELS.BORDER_START_KEY,
                    },
                  ],
                },
              },
              {
                id: ComponentSettingKey.SectionDesignBorderWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.BORDER_WIDTH_KEY,
                defaultValue: [0],
                sliderSettings: {
                  max: 25,
                  min: 0,
                  step: 1,
                  range: false,
                },
                forceHorizontal: true,
                dependOn: {
                  id: ComponentSettingKey.SectionDesignBorder,
                  showWhen: (value?: unknown) => {
                    return Array.isArray(value) ? value.length > 0 : false
                  },
                },
              },
              {
                id: ComponentSettingKey.SectionDesignBorderColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.BORDER_COLOR_KEY,
                colorVariantSettings: {
                  schemeId: '',
                  fullColor: 'both',
                },
                defaultValue: 'primary-70',
                forceHorizontal: true,
                dependOn: {
                  id: ComponentSettingKey.SectionDesignBorder,
                  showWhen: (value?: unknown) => {
                    return Array.isArray(value) ? value.length > 0 : false
                  },
                },
              },
              {
                id: ComponentSettingKey.SectionDesignBorderStyle,
                type: BuilderComponentSettingType.Select,
                label: LABELS.BORDER_STYLE_KEY,
                selectSettings: {
                  options: [
                    {
                      value: 'solid',
                      label: 'Solid',
                    },
                    {
                      value: 'dotted',
                      label: 'Dotted',
                    },
                    {
                      value: 'dashed',
                      label: 'Dashed',
                    },
                  ],
                },
                forceHorizontal: true,
                dependOn: {
                  id: ComponentSettingKey.SectionDesignBorder,
                  showWhen: (value?: unknown) => {
                    return Array.isArray(value) ? value.length > 0 : false
                  },
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: makeSpacing(
            {
              padding: {
                id: ComponentSettingKey.SectionDesignPadding,
                defaultValue: { y: 20, x: 20 },
              },
              margin: {
                id: ComponentSettingKey.SectionDesignMargin,
              },
            },
            [
              {
                type: BuilderComponentSettingType.SpacingSingle,
                label: LABELS.ITEM_GAP_KEY,
                id: ComponentSettingKey.SectionDesignGap,
              },
            ]
          ),
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.SectionDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
          /*Orientation: {
              items: [
                {
                  id: ComponentSettingKey.SectionDesignOrientation,
                  label: 'Orientation',
                  type: BuilderComponentSettingType.IconSelect,
                  iconSelectSettings: {
                    options: [
                      {
                        id: 'horizontal',
                        icon: 'Horizontal24',
                        title: LABELS.HORIZONTAL_KEY
                      },
                      {
                        id: 'vertical',
                        icon: 'Vertical24',
                        title: LABELS.VERTICAL_KEY
                      }
                    ]
                  }
                }
              ]
            },*/
        },
        behavior: {
          visibility: true,
        },
      },
    },
    {
      compId: 'tabs',
      title: LABELS.TABS_KEY,
      searchKeywords: ['tabs', 'navigation', 'segmented control', 'pill menu', 'switcher', 'toggle view', 'tab bar'],
      icon: 'Tab24',
      nested: true,
      settings: {
        dataGroup: {
          [LABELS.TAB_ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsData,
                label: '',
                type: BuilderComponentSettingType.TabsItems,
                defaultValue: {},
              },
            ],
          },
          [LABELS.DISPLAY_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDataShowIcon,
                label: LABELS.SHOW_ICON_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
              },
              {
                id: ComponentSettingKey.TabsDataShowLabel,
                label: LABELS.SHOW_LABEL_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
              },
            ],
          },
        },
        designGroup: {
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.TabsDesignContentAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: LABELS.CONTENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.COLOR_VARIANT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Background Color',
                defaultValue: 'primary-80',
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: makeClampWidthItems(ComponentSettingKey.TabsDesignWidth),
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignShape,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.ORIENTATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignOrientation,
                label: 'Orientation',
                type: BuilderComponentSettingType.IconSelect,
                defaultValue: 'horizontal',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: 'Horizontal',
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: 'Vertical',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.SHADOW_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignContentShadow,
                type: BuilderComponentSettingType.Select,
                label: LABELS.SHADOW_KEY,
                defaultValue: 'medium',
                selectSettings: {
                  options: [
                    { value: 'none', label: 'None' },
                    { value: 'sm', label: 'Small' },
                    { value: 'lg', label: 'Medium' },
                    { value: 'xl', label: 'Large' },
                  ],
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_MARGIN_KEY,
                defaultValue: {
                  top: { value: 16, unit: SpacingInputUnit.Pixel },
                  bottom: { value: 16, unit: SpacingInputUnit.Pixel },
                  left: { value: 16, unit: SpacingInputUnit.Pixel },
                  right: { value: 16, unit: SpacingInputUnit.Pixel },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 2,
                //     [SpacingInputUnit.Rem]: 2,
                //     [SpacingInputUnit.Pixel]: 100,
                //     [SpacingInputUnit.Percent]: 5,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.TabsDesignPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Tab Padding',
                defaultValue: {
                  top: { value: 0, unit: SpacingInputUnit.Pixel },
                  bottom: { value: 0, unit: SpacingInputUnit.Pixel },
                  left: { value: 0, unit: SpacingInputUnit.Pixel },
                  right: { value: 0, unit: SpacingInputUnit.Pixel },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 2,
                //     [SpacingInputUnit.Rem]: 2,
                //     [SpacingInputUnit.Pixel]: 100,
                //     [SpacingInputUnit.Percent]: 5,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.TabsDesignItemPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Tab Item Padding',
                defaultValue: {
                  top: { value: 8, unit: SpacingInputUnit.Pixel },
                  bottom: { value: 8, unit: SpacingInputUnit.Pixel },
                  left: { value: 12, unit: SpacingInputUnit.Pixel },
                  right: { value: 12, unit: SpacingInputUnit.Pixel },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 2,
                //     [SpacingInputUnit.Rem]: 2,
                //     [SpacingInputUnit.Pixel]: 100,
                //     [SpacingInputUnit.Percent]: 5,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.TabsDesignContentPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Content Padding',
                defaultValue: {
                  top: { value: 16, unit: SpacingInputUnit.Pixel },
                  bottom: { value: 16, unit: SpacingInputUnit.Pixel },
                  left: { value: 16, unit: SpacingInputUnit.Pixel },
                  right: { value: 16, unit: SpacingInputUnit.Pixel },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 2,
                //     [SpacingInputUnit.Rem]: 2,
                //     [SpacingInputUnit.Pixel]: 100,
                //     [SpacingInputUnit.Percent]: 5,
                //   },
                // },
              },
            ],
          },
          [LABELS.VARIANT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignVariant,
                label: LABELS.VARIANT_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: 'default',
                selectSettings: {
                  options: [
                    {
                      value: 'default',
                      label: 'Default',
                    },
                    {
                      value: 'underlined',
                      label: 'Underlined',
                    },
                    {
                      value: 'pills',
                      label: 'Pills',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TabsDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        behavior: {
          visibility: true,
        },
      },
    },
    {
      compId: 'form',
      title: 'Form',
      searchKeywords: [
        'form',
        'form layout',
        'input group',
        'questionnaire',
        'fields',
        'data entry',
        'submission',
        'application form',
      ],
      icon: 'Form24',
      nested: true,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FormDataSource,
                label: LABELS.DATA_SOURCE_KEY,
                type: BuilderComponentSettingType.TableSelect,
                help: '',
                tableSelectSettings: {
                  disabledWhenDetailScreen: true,
                  includeSnapshot: false,
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FormDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                autoDefaultInDetailPage: false,
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FormDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.COMPONENT_TITLE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.FormDesignTitleColor,
                      ComponentSettingKey.FormDesignTitleColorSolid,
                      ComponentSettingKey.FormDesignTitleColorStartGradient,
                      ComponentSettingKey.FormDesignTitleColorEndGradient,
                      ComponentSettingKey.FormDesignTitleColorGradientDirection,
                      ComponentSettingKey.FormDesignScheme,
                      LABELS.COMPONENT_TITLE_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LABEL_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.FormDesignLabelColor,
                      ComponentSettingKey.FormDesignLabelColorSolid,
                      ComponentSettingKey.FormDesignLabelColorStartGradient,
                      ComponentSettingKey.FormDesignLabelColorEndGradient,
                      ComponentSettingKey.FormDesignLabelColorGradientDirection,
                      ComponentSettingKey.FormDesignScheme,
                      LABELS.LABEL_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FormDesignFieldShape,
                type: BuilderComponentSettingType.Select,
                label: LABELS.FIELD_KEY,
                defaultValue: MdShape.Small,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          // [LABELS.STYLE_KEY]: {
          //   items: [
          //     // {
          //     //   id: ComponentSettingKey.FormDesignOutlined,
          //     //   type: BuilderComponentSettingType.Switch,
          //     //   label: 'Outlined',
          //     //   defaultValue: true,
          //     // },
          //     {
          //       id: ComponentSettingKey.FormDesignVariant,
          //       type: BuilderComponentSettingType.Select,
          //       label: LABELS.VARIANT_KEY,
          //       defaultValue: 'filled-outlined',
          //       selectSettings: {
          //         options: [
          //           {
          //             value: 'outlined',
          //             label: 'Transparent Form with Floating Labels',
          //           },
          //           {
          //             value: 'filled',
          //             label: 'Filled Form with Bottom Border Only',
          //           },
          //           {
          //             value: 'filled-outlined',
          //             label: 'Filled Form with Full Border',
          //           },
          //         ],
          //       },
          //     },
          //   ],
          // },
          [LABELS.DIMENSION_KEY]: makeClampWidthItems(ComponentSettingKey.FormDesignWidth),
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FormDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.FormDesignCompTitleAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.COMPONENT_TITLE_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.FormDesignMargin,
                label: LABELS.COMPONENT_MARGIN_KEY,
                type: BuilderComponentSettingType.Spacing,
              },
              {
                id: ComponentSettingKey.FormDesignPadding,
                label: LABELS.COMPONENT_PADDING_KEY,
                type: BuilderComponentSettingType.Spacing,
              },
              {
                id: ComponentSettingKey.FormDesignFieldMargin,
                label: LABELS.FIELD_PADDING_KEY,
                type: BuilderComponentSettingType.Spacing,
              },
              {
                id: ComponentSettingKey.FormDesignCompTitleMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_TITLE_MARGIN_KEY,
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.FormDesignTypography, [
            // {
            //   id: ComponentSettingKey.FormDesignLabelTypography,
            //   label: LABELS.LABEL_KEY,
            //   defaultValue: 'Label Medium',
            // },
            // {
            //   id: ComponentSettingKey.FormDesignInputTypography,
            //   label: LABELS.INPUT_KEY,
            //   defaultValue: 'Body Medium',
            // },
            // {
            //   id: ComponentSettingKey.FormDesignHelpTypography,
            //   label: LABELS.HELP_TEXT_KEY,
            //   defaultValue: 'Body Small',
            // },
            {
              id: ComponentSettingKey.FormDesignCompTitleTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'Title Medium',
            },
          ]),
        },
        behavior: {
          visibility: true,
        },
      },
    },
  ],
}
